import { toRaw } from 'vue'

// Offline-first photo queue for the field photos page.
// Photos are compressed and saved on the phone (IndexedDB) the moment they are taken, then uploaded
// one by one. Nothing is lost without signal: the queue resumes when the connection or the page comes back.
// Each photo carries a client_uuid, so a retry after a dropped connection never creates a duplicate.

export interface QueueTarget {
  kind: 'project' | 'field_project'
  id: number
}

export interface QueuedPhoto {
  ownerKey?: string
  client_uuid: string
  // Null until the worker picks the job (the app opens on the camera, the job comes after).
  target: QueueTarget | null
  // The department the photo was taken for (Mitigation, Recon, Packout).
  department: string | null
  blob: Blob
  latitude: number | null
  longitude: number | null
  taken_at: string
  created_at: number
  attempts: number
  error: string | null
  failed: boolean
}

const DB_NAME = 'field-photos'
const STORE = 'uploads'
const MAX_SIDE = 2000
const JPEG_QUALITY = 0.82

let dbPromise: Promise<IDBDatabase> | null = null

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE, { keyPath: 'client_uuid' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      dbPromise = null
      reject(request.error)
    }
  })
  return dbPromise
}

const run = async <T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const request = action(db.transaction(STORE, mode).objectStore(STORE))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export const queuedPhotos = async (): Promise<QueuedPhoto[]> => {
  const items = await run<QueuedPhoto[]>('readonly', (store) => store.getAll())
  return items.sort((a, b) => a.created_at - b.created_at)
}

export const saveQueuedPhoto = (item: QueuedPhoto) => {
  // Items shown in the UI come from Vue reactive state, whose nested objects are Proxies.
  // IndexedDB can't store a Proxy (DataCloneError), so always save a plain copy.
  const raw = toRaw(item)
  const plain: QueuedPhoto = {
    ...raw,
    target: raw.target ? { ...toRaw(raw.target) } : null,
  }
  return run('readwrite', (store) => store.put(plain))
}

export const removeQueuedPhoto = (clientUuid: string) =>
  run('readwrite', (store) => store.delete(clientUuid))

const loadImage = async (
  file: Blob
): Promise<CanvasImageSource & { width: number; height: number }> => {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      // Older Safari: fall back to an <img> element below.
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Shrink a camera photo (often 4-8 MB) to a ~300-600 KB JPEG. Falls back to the original file. */
export const compressPhoto = async (file: File): Promise<Blob> => {
  try {
    const image = await loadImage(file)
    const scale = Math.min(1, MAX_SIDE / Math.max(image.width, image.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(image.width * scale)
    canvas.height = Math.round(image.height * scale)
    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    if ('close' in image && typeof image.close === 'function') image.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    )
    return blob && blob.size < file.size ? blob : file
  } catch {
    return file
  }
}

export const newClientUuid = (): string => {
  if (crypto.randomUUID) return crypto.randomUUID()
  // crypto.randomUUID needs a secure context and a recent browser.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// Errors where retrying the same file can't help (bad file, job removed, too large).
const PERMANENT_STATUSES = [400, 403, 404, 413]

/**
 * Upload queued photos one at a time. Returns how many were uploaded.
 * Stops at the first network error (no signal): the rest stay queued for the next run.
 */
export const uploadQueuedPhotos = async (
  upload: (body: FormData) => Promise<unknown>,
  onProgress?: () => void,
  belongs: (item: QueuedPhoto) => boolean = () => true
): Promise<number> => {
  let uploaded = 0
  for (const item of await queuedPhotos()) {
    if (!belongs(item) || item.failed || !item.target) continue

    const body = new FormData()
    body.append('file', item.blob, `${item.client_uuid}.jpg`)
    body.append(item.target.kind, String(item.target.id))
    body.append('client_uuid', item.client_uuid)
    body.append('taken_at', item.taken_at)
    if (item.department) body.append('department', item.department)
    if (item.latitude !== null && item.longitude !== null) {
      body.append('latitude', item.latitude.toFixed(6))
      body.append('longitude', item.longitude.toFixed(6))
    }

    try {
      await upload(body)
      await removeQueuedPhoto(item.client_uuid)
      uploaded += 1
      onProgress?.()
    } catch (error) {
      const status = (
        error as { status?: number; response?: { status?: number } }
      )?.response?.status
      item.attempts += 1
      item.error = fieldErrorMessage(error)
      item.failed = status !== undefined && PERMANENT_STATUSES.includes(status)
      await saveQueuedPhoto(item)
      onProgress?.()
      if (!item.failed) break
    }
  }
  return uploaded
}

// Last job list, kept on the phone so jobs can be picked without signal.
const JOBS_CACHE_KEY = 'field-jobs-cache'

export const saveJobsCache = (places: FieldPlace[]) => {
  try {
    localStorage.setItem(JOBS_CACHE_KEY, JSON.stringify(places))
  } catch {
    // Storage full or blocked: the list just won't be available offline.
  }
}

export const loadJobsCache = (): FieldPlace[] => {
  try {
    return JSON.parse(
      localStorage.getItem(JOBS_CACHE_KEY) ?? '[]'
    ) as FieldPlace[]
  } catch {
    return []
  }
}
